import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { LoadingSpinner } from '@liminal/web-ui/feedback/LoadingSpinner';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { Tabs } from '@liminal/web-ui/primitives/Tabs';
import { getErrorMessage } from '@/errors/messages';
import { useEventCode, useEventTally, useSeenEvent } from '@/hooks/useEvents';
import { useNow } from '@/hooks/useNow';
import { eventStatus, tallyText } from '../checkIn';

/** One column at phone width, centred on a laptop: the page is held at a door. */
const COLUMN_MAX_WIDTH = 560;

/**
 * Tighter on a phone, so the camera and the guest card under it share one
 * screen and staff do not scroll for every guest.
 */
const PHONE_SPACING = { xs: 2, sm: 3 } as const;

/** How often the header re-reads the clock, so "Open until" turns to "Closed" on time. */
const CLOCK_TICK_MS = 30_000;

/**
 * An event's door (LIM-4707): its name, whether check-in is open, the running
 * tally, and two tabs, the check-in itself and every guest handed tickets.
 */
export function EventPage() {
  const code = useEventCode();
  const location = useLocation();
  const navigate = useNavigate();
  const now = useNow(CLOCK_TICK_MS);
  const event = useSeenEvent(code).data;
  const tally = useEventTally(code);

  const tabs = [
    { value: `/events/${code}/check-in`, label: 'Check-in' },
    { value: `/events/${code}/issuances`, label: 'Issuances' },
  ];
  const active = tabs.find((tab) => location.pathname === tab.value) ?? tabs[0];
  const status = event === undefined ? null : eventStatus(event, now);

  return (
    <Stack spacing={PHONE_SPACING} sx={{ width: '100%', maxWidth: COLUMN_MAX_WIDTH, mx: 'auto' }}>
      <Stack spacing={1}>
        {/* The event's name and window come with the first guest read (no
            route reads an event on its own); until then the page knows only
            its code, and says so rather than guess a window. */}
        <Typography
          variant="h4"
          component="h1"
          sx={{ typography: { xs: 'h5', sm: 'h4' }, overflowWrap: 'anywhere' }}
          data-testid="event-name"
        >
          {event?.displayName ?? code}
        </Typography>
        <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center">
          {status ? (
            <StatusPill label={status.label} tone={status.open ? 'success' : 'neutral'} data-testid="event-status" />
          ) : (
            <Typography variant="body2" color="text.secondary" data-testid="event-status">
              Scan the first guest to load the event.
            </Typography>
          )}
          {tally.data ? (
            <Typography variant="h5" data-testid="event-tally">
              {tallyText(tally.data.guests, tally.data.tickets)}
            </Typography>
          ) : null}
          {tally.isPending ? <LoadingSpinner variant="inline" /> : null}
        </Stack>
        {tally.isError ? (
          <WarningCard tone="error" action={{ label: 'Try again', onClick: () => void tally.refetch() }}>
            The tally did not load. {getErrorMessage(tally.error)}
          </WarningCard>
        ) : null}
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={active.value}
          onChange={navigate}
          aria-label="Event sections"
          items={tabs}
        />
      </Box>

      <Outlet />
    </Stack>
  );
}
